
import React, { useState } from 'react';
import { FlaskConical, RefreshCw, Settings2, Bug, Folder, FolderCheck, FolderSync, AlertTriangle, LogOut, KeyRound, X, Check, Activity } from 'lucide-react';
import JobManagerIcon from '../JobManagerIcon';
import ErrorLogPanel from './ErrorLogPanel';
import { FileSystemStatus } from '../../services/shared/fileSystemPersistence';
import { useAuth } from '../../contexts/AuthContext';
import { useTokenBalance } from '../../hooks/useTokenBalance';
import { isCommercial, isInternal } from '../../config/appMode';
import PrivacyBadge from '../commercial/PrivacyBadge';
import Breadcrumb from './Breadcrumb';
import type { BreadcrumbItem } from '../../hooks/useHashRouter';

interface HeaderProps {
    onReset: () => void;
    onToggleDebug?: () => void;
    showDebug?: boolean;
    onToggleJobs?: () => void;
    fsStatus?: FileSystemStatus;
    onConnectFS?: () => void;
    costDisplay?: React.ReactNode;
    onSelectFile?: (fileId: string) => void;
    onSecretClick?: (e: React.MouseEvent) => void;
    breadcrumbItems?: BreadcrumbItem[];
    onBreadcrumbNavigate?: (hash: string) => void;
    onToggleJobsDrawer?: () => void;
    jobsRunningCount?: number;
}

const Header: React.FC<HeaderProps> = ({
    onReset, onToggleDebug, showDebug, onToggleJobs,
    fsStatus, onConnectFS, costDisplay, onSelectFile, onSecretClick,
    breadcrumbItems, onBreadcrumbNavigate,
    onToggleJobsDrawer, jobsRunningCount = 0,
}) => {
    const { user, profile, signOut, updatePassword } = useAuth();
    const { tokenBalance, isLow, isEmpty, formatBalance } = useTokenBalance();
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const handleChangePassword = async () => {
        setPasswordError(null);
        setPasswordSuccess(false);

        if (!newPassword || newPassword.length < 6) {
            setPasswordError('Mật khẩu phải có ít nhất 6 ký tự');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setPasswordError('Mật khẩu xác nhận không khớp');
            return;
        }

        setIsUpdating(true);
        const { error } = await updatePassword(newPassword);
        setIsUpdating(false);

        if (error) {
            setPasswordError('Đổi mật khẩu thất bại. Vui lòng thử lại');
            return;
        }

        setPasswordSuccess(true);
        setNewPassword('');
        setConfirmNewPassword('');
        setTimeout(() => {
            setShowChangePassword(false);
            setPasswordSuccess(false);
        }, 1500);
    };

    return (
        <header className="h-14 border-b border-slate-200 flex items-center justify-between px-6 bg-white shrink-0 z-40">
            <div className="flex items-center gap-4">
                {breadcrumbItems && onBreadcrumbNavigate ? (
                    <Breadcrumb items={breadcrumbItems} onNavigate={onBreadcrumbNavigate} />
                ) : (
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Dự án</span>
                        <span className="text-slate-200">/</span>
                        <span className="text-sm font-bold text-slate-700">{fsStatus?.folderName || 'Local'}</span>
                    </div>
                )}
            </div>
            <div className="flex gap-3 items-center">
                {/* Commercial: Hoạt động (jobs drawer trigger) */}
                {isCommercial && onToggleJobsDrawer && (
                    <button
                        onClick={onToggleJobsDrawer}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white text-slate-700 text-xs font-semibold rounded-lg border border-slate-200 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50/40 transition-colors"
                        title="Xem hoạt động đang chạy"
                    >
                        <Activity className="w-4 h-4" />
                        Hoạt động
                        {jobsRunningCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full">{jobsRunningCount}</span>
                        )}
                    </button>
                )}

                {/* Commercial: Privacy badge */}
                {isCommercial && <PrivacyBadge variant="header" />}

                {/* Internal-only: Job manager + Error logs + Debug */}
                {isInternal && onToggleJobs && (
                    <JobManagerIcon onClick={onToggleJobs} />
                )}
                {isInternal && <ErrorLogPanel onSelectFile={onSelectFile} />}
                {isInternal && onToggleDebug && profile?.role === 'admin' && (
                    <button
                        onClick={onToggleDebug}
                        className={`p-2 rounded-lg ${showDebug ? 'text-amber-600 bg-amber-50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
                        title="Debug Lab"
                    >
                        <Bug className="w-5 h-5" />
                    </button>
                )}

                <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>

                {/* Internal: cost display + FS connector + token balance */}
                {isInternal && costDisplay}

                {isInternal && onConnectFS && (
                    <button
                        onClick={onConnectFS}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${fsStatus?.isConnected
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : fsStatus?.error
                                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                            }`}
                        title={fsStatus?.isConnected ? `Đã kết nối ${fsStatus.folderName}` : "Kết nối thư mục"}
                    >
                        {fsStatus?.isConnected ? (
                            <FolderCheck className="w-4 h-4" />
                        ) : fsStatus?.error ? (
                            <AlertTriangle className="w-4 h-4" />
                        ) : (
                            <Folder className="w-4 h-4" />
                        )}
                        <span className="hidden md:inline">
                            {fsStatus?.isConnected ? fsStatus.folderName : "Thư mục"}
                        </span>
                    </button>
                )}

                {/* Internal: Token balance (old system) */}
                {isInternal && tokenBalance && (
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${
                        isEmpty ? 'bg-red-50 text-red-600' :
                        isLow ? 'bg-amber-50 text-amber-600' :
                        'bg-emerald-50 text-emerald-600'
                    }`} title={`Token con lai: ${tokenBalance.balance.toLocaleString()}`}>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formatBalance(tokenBalance.balance)}
                    </div>
                )}

                {/* Commercial: Credit badge — ĐÃ CHUYỂN XUỐNG SIDEBAR */}

                {/* User area — chỉ hiện cho internal, commercial dùng sidebar footer */}
                {!isCommercial && <div className="h-4 w-[1px] bg-slate-200 mx-1"></div>}
                <div className={`flex items-center gap-1 relative ${isCommercial ? 'hidden' : ''}`}>
                    {(profile || user) && (
                        <span className="text-xs text-slate-400 hidden md:inline mr-1">
                            {profile?.display_name || user?.email}
                            {profile?.role === 'admin' && (
                                <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold uppercase">Admin</span>
                            )}
                        </span>
                    )}
                    <button
                        onClick={(e) => {
                          if (e.ctrlKey && e.shiftKey) {
                            // Secret dev mode click -- khong mo dropdown
                            if (onSecretClick) onSecretClick(e);
                            return;
                          }
                          setShowChangePassword(!showChangePassword);
                          setPasswordError(null); setPasswordSuccess(false);
                          setNewPassword(''); setConfirmNewPassword('');
                        }}
                        className={`p-2 rounded-lg transition-colors ${showChangePassword ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                        title="Đổi mật khẩu"
                    >
                        <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                        onClick={signOut}
                        className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors"
                        title="Đăng xuất"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>

                    {/* Change password dropdown */}
                    {showChangePassword && (
                        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-bold text-slate-700">Đổi mật khẩu</h3>
                                <button onClick={() => setShowChangePassword(false)} className="text-slate-400 hover:text-slate-600">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {passwordSuccess ? (
                                <div className="flex items-center gap-2 py-3 text-emerald-600 text-sm font-semibold">
                                    <Check className="w-4 h-4" />
                                    Đổi mật khẩu thành công!
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Mật khẩu mới"
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <input
                                        type="password"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        placeholder="Xác nhận mật khẩu mới"
                                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    {passwordError && (
                                        <p className="text-xs text-red-500">{passwordError}</p>
                                    )}
                                    <button
                                        onClick={handleChangePassword}
                                        disabled={isUpdating}
                                        className={`w-full py-2 rounded-lg text-sm font-bold transition-all ${
                                            isUpdating
                                                ? 'bg-indigo-300 text-white cursor-wait'
                                                : 'bg-indigo-600 text-white hover:bg-indigo-500 active:scale-[0.98]'
                                        }`}
                                    >
                                        {isUpdating ? 'Đang cập nhật...' : 'Xác nhận'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
